/**
 * What the connection banner says, and when it says nothing.
 *
 * `docs/07` §3 opens with the rule this file implements: **the socket is
 * invisible when healthy.** Eleven rows, one of which is "say nothing", and the
 * order they are checked in is a product decision rather than an implementation
 * detail — a user who is offline and whose session expired needs to be told
 * about the network, because signing in is not a thing they can currently do.
 *
 * A pure function for the same reason `order-validation.ts` is one: the copy is
 * specified, the precedence is arguable, and both are cheaper to review as a
 * table of cases than as a chain of ternaries inside a component. No React, no
 * DOM, no `Date.now()` — `sinceMs` arrives as a duration the caller measured
 * (invariants 7 and 10).
 *
 * **Two seconds is a banner rule, not a trading rule.** `docs/07` says a
 * reconnect under two seconds is not an event and shows nothing; `docs/03`
 * §9.2.1 says order entry disables at **0 ms**, because trading off a book you
 * know you are not receiving updates for is the one thing worse than not
 * trading. Those are not in tension and this file implements only the first —
 * `<OrderTicket>`'s `socketStale` is not routed through here.
 */

import type { ConnectionStatus } from '@/api/ws/connection'
import type { WsErrorCode } from '@/api/ws/protocol'

export type NoticeTone = 'info' | 'warning' | 'error'

export type NoticeCode =
  | 'OFFLINE'
  | 'GEO_RESTRICTED'
  | 'SLOW_CONSUMER'
  | 'CONNECTION_LIMIT'
  | 'RECONNECTING'
  | 'SESSION_EXPIRED'
  | 'VENUE_HALTED'
  | 'VENUE_MAINTENANCE'
  | 'VENUE_DEGRADED'
  | 'RECONNECTED'

/** The one inline affordance the table specifies. */
export interface NoticeAction {
  kind: 'SIGN_IN'
  label: string
}

export interface ConnectionNotice {
  code: NoticeCode
  tone: NoticeTone
  message: string
  action?: NoticeAction
  /** Retire the banner after this long. `null` means "until the cause clears". */
  autoDismissMs: number | null
}

export interface ConnectionNoticeInput {
  status: ConnectionStatus
  /**
   * How long the connection has been up, or down — `connectionClockAfter`'s
   * clock, on which `BACKOFF` and `CONNECTING` are one outage. Only the
   * reconnect rows read it, and both thresholds are `docs/07`'s.
   */
  statusForMs: number
  /** `navigator.onLine`. A browser that does not report it is treated as online:
   *  a false "You're offline." on a working connection is the worse error. */
  online: boolean
  /**
   * This `OPEN` followed a connection that had been lost — as opposed to the
   * first open of the tab's life.
   *
   * Without it the cold-load path shows `Reconnected.` to a user who has not
   * been connected to anything yet, which reads as a failure they missed.
   */
  resumed: boolean
  /** The session behind the socket expired and the client went anonymous. */
  sessionExpired: boolean
  /** `heartbeat.venue_status`, rolled up from `GET /health`. */
  venueStatus: 'OK' | 'DEGRADED' | 'HALTED' | 'MAINTENANCE' | null
  /**
   * The last terminal `error` frame's code, if the client has not resynced past
   * it. `SLOW_CONSUMER`, `CONNECTION_LIMIT` and `GEO_RESTRICTED` have copy of their own;
   * `SERVER_SHUTDOWN` deliberately has none, because a planned deploy must be
   * indistinguishable from any other reconnect (`docs/07` §3).
   */
  lastProblem: WsErrorCode | null
}

/** `docs/07` §3: under two seconds a reconnect is a blink, not an event. */
export const RECONNECTING_AFTER_MS = 2_000

/**
 * When the connection's clock restarts: when the socket comes up or goes down,
 * and at the first status of a tab's life — never between two down statuses.
 *
 * `BACKOFF` and `CONNECTING` are one outage to the person looking at the page,
 * and `docs/07` §3's two seconds are two seconds *disconnected*. Until
 * 2026-09-30 the clock restarted on every status, so `Reconnecting…` showed
 * from two seconds into a backoff until the reconnect attempt began and then
 * vanished while the page was still disconnected — and when the two-second
 * render lost the race with that attempt, as it did under load, it never showed
 * at all.
 */
export function connectionClockAfter(
  previous: ConnectionStatus,
  next: ConnectionStatus,
  sinceMs: number,
  nowMs: number,
): number {
  if (previous === 'IDLE' || (previous === 'OPEN') !== (next === 'OPEN')) return nowMs
  return sinceMs
}
/** How long "Reconnected." stays up once the socket is back. */
export const RECONNECTED_FOR_MS = 2_000

export function connectionNotice(input: ConnectionNoticeInput): ConnectionNotice | null {
  const open = input.status === 'OPEN'

  // The network first, and above everything: every other row below tells the
  // user about something happening on a connection they do not currently have.
  if (!input.online) {
    return { code: 'OFFLINE', tone: 'error', message: "You're offline.", autoDismissMs: null }
  }

  // Jurisdiction outranks the session because it does not resolve by signing in,
  // and offering [Sign in] to someone who cannot use the account surface at all
  // is an invitation into a wall.
  if (input.lastProblem === 'GEO_RESTRICTED') {
    return {
      code: 'GEO_RESTRICTED',
      tone: 'error',
      message: "Account data isn't available in your location.",
      autoDismissMs: null,
    }
  }

  // Named while the repair is in flight, and only then: `SLOW_CONSUMER` is a
  // *subscription* failure, so the socket is usually still `OPEN` and the client
  // is re-snapshotting one channel behind a `STALE` pill.
  if (input.lastProblem === 'SLOW_CONSUMER') {
    return {
      code: 'SLOW_CONSUMER',
      tone: 'warning',
      message: "Your connection can't keep up with this market. Reconnecting.",
      autoDismissMs: null,
    }
  }

  // The one refusal a user can fix, and the socket will not say so itself: the
  // tab retries on the `1013`'s hint, so without this it reads `Reconnecting…`
  // for ever. `Runtime.#onStatus` clears it when a socket opens, and a socket
  // refused again sets it again a moment later.
  if (input.lastProblem === 'CONNECTION_LIMIT') {
    return {
      code: 'CONNECTION_LIMIT',
      tone: 'warning',
      message: 'Too many UpTik tabs are open on this network. Close one to reconnect.',
      autoDismissMs: null,
    }
  }

  // **Maintenance survives the reconnect it causes** (`docs/06` §F132). A
  // release restarts the processes behind the socket inside its maintenance
  // minute, so the socket drops and comes back while the venue is still down.
  // The last thing the venue said was MAINTENANCE and nothing has contradicted
  // it; `Reconnecting…`, or two seconds of nothing, would read as a new fault in
  // the middle of a planned one. The status is not cleared on a drop
  // (`Runtime.#onStatus`), so the first heartbeat on the new socket is what
  // replaces it.
  if (!open && input.venueStatus === 'MAINTENANCE') {
    return {
      code: 'VENUE_MAINTENANCE',
      tone: 'warning',
      message: 'The exchange is under maintenance. Your open positions will still settle.',
      autoDismissMs: null,
    }
  }

  if (!open && input.statusForMs >= RECONNECTING_AFTER_MS) {
    // `SERVER_SHUTDOWN` lands here too, with the same three words. A user cannot
    // act on the difference between a deploy and a dropped Wi-Fi packet, and a
    // banner that named the deploy would make a routine release look like an
    // incident sixty times a day.
    return { code: 'RECONNECTING', tone: 'warning', message: 'Reconnecting…', autoDismissMs: null }
  }

  // Below the two-second line and not yet back: say nothing at all.
  if (!open) return null

  /*
    **The kill switch outranks the session, and `DEGRADED` does not.**

    A halted venue is not something signing in fixes, so offering `[Sign in]`
    above it is the invitation-into-a-wall `GEO_RESTRICTED` already argued
    against. `DEGRADED` is the opposite case — trading continues, so getting the
    account back is exactly the useful next act — and it stays below.

    **Phase 10 wrote these two rows and Phase 9 deliberately did not.** `docs/14`
    §4 records why: §9.2.12 puts the halt on the *ticket*, and inventing banner
    copy was the spec owner's call. `docs/01` §Phase 10 asks for "a kill-switch
    banner driven by `heartbeat.venue_status`", which is that call being made.

    Neither line carries `estimated_resume_ms`. That number reaches the ticket,
    whose job is to explain a button that will not press; a banner counting down
    to a time the venue then missed would turn one incident into two.
  */
  if (input.venueStatus === 'HALTED') {
    return {
      code: 'VENUE_HALTED',
      tone: 'error',
      message: 'Trading is halted. Your open positions will still settle.',
      autoDismissMs: null,
    }
  }

  if (input.venueStatus === 'MAINTENANCE') {
    return {
      code: 'VENUE_MAINTENANCE',
      tone: 'warning',
      message: 'The exchange is under maintenance. Your open positions will still settle.',
      autoDismissMs: null,
    }
  }

  if (input.sessionExpired) {
    return {
      code: 'SESSION_EXPIRED',
      tone: 'info',
      message: 'Your session expired.',
      action: { kind: 'SIGN_IN', label: 'Sign in' },
      autoDismissMs: null,
    }
  }

  if (input.venueStatus === 'DEGRADED') {
    return {
      code: 'VENUE_DEGRADED',
      tone: 'warning',
      message: 'The exchange is running degraded. Trading continues.',
      autoDismissMs: null,
    }
  }

  if (input.resumed && input.statusForMs < RECONNECTED_FOR_MS) {
    return { code: 'RECONNECTED', tone: 'info', message: 'Reconnected.', autoDismissMs: RECONNECTED_FOR_MS }
  }

  return null
}
